/**
 * SOLOMON — Client-side image downscale (chat image attachments)
 *
 * Bounds vision token cost before upload: anything over MAX_LONG_EDGE px on
 * its long edge is canvas-downscaled and re-encoded as JPEG before it rides
 * the chat POST. Images already within bounds pass through byte-identical
 * (no recompression loss). Animated GIFs flatten to their first frame —
 * canvas semantics, acceptable v1.
 */

// Claude's optimal vision long edge; GPT-4o and Gemini handle this size well.
export const MAX_LONG_EDGE = 1568;

const JPEG_QUALITY = 0.85;

// Extension → media type fallback for files the browser gives no MIME for.
const EXT_MEDIA_TYPES = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
};

function inferMediaType(file) {
  if (file.type) return file.type;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return EXT_MEDIA_TYPES[ext] || 'application/octet-stream';
}

function blobToDataBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL yields "data:<mime>;base64,<data>" — strip the prefix.
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('Could not read image file.'));
    reader.readAsDataURL(blob);
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Image re-encode failed.'))),
      type,
      quality,
    );
  });
}

/**
 * Prepare one image file for the chat POST.
 * @param {File} file
 * @returns {Promise<{ name: string, mediaType: string, dataBase64: string, byteSize: number }>}
 * @throws on unreadable/corrupt image — caller surfaces via its onError path.
 */
export async function prepareImageAttachment(file) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(`Couldn't read "${file.name}" — the image may be corrupt.`);
  }

  try {
    const longEdge = Math.max(bitmap.width, bitmap.height);

    if (longEdge <= MAX_LONG_EDGE) {
      // Within bounds — pass the original bytes through untouched.
      return {
        name: file.name,
        mediaType: inferMediaType(file),
        dataBase64: await blobToDataBase64(file),
        byteSize: file.size,
      };
    }

    const scale = MAX_LONG_EDGE / longEdge;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    // JPEG has no alpha — flatten any transparency onto white, not black.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
    return {
      name: file.name,
      mediaType: 'image/jpeg',
      dataBase64: await blobToDataBase64(blob),
      byteSize: blob.size,
    };
  } finally {
    bitmap.close();
  }
}
